export type GerenciarPortalOperacao = 'I' | 'A' | 'E'

export type GerenciarPortalBody = {
  tipoOperacao: GerenciarPortalOperacao
  codCorretor: number
  codUsuarioWiz: number
  codContato?: number
  nomContato?: string
  codDepartamento?: number
  indSocio?: 'S' | 'N'
  dscCargo?: string
  numTelefone?: string
  numWhatsapp?: string
  dscEmails?: string
}

export type BrokerContactForScgp = {
  contact_code: number | null
  name: string | null
  department: string | null
  role: string | null
  phone: string | null
  email: string | null
  is_partner: boolean | null
}

export type GerenciarPortalResult = {
  tipoOperacao: GerenciarPortalOperacao
  codContato?: number
  rawResponse: unknown
}

export type ScgpContactRequestType =
  | 'contact_create'
  | 'contact_update'
  | 'contact_set_active'
  | 'contact_delete'

type ScgpChangePayload = Record<string, unknown>

/** Base sem barra final; remove sufixo `/api` para evitar `/api/api/...` nas rotas. */
function normalizeScgpApiBase(raw: string): string {
  const trimmed = raw.trim().replace(/\/$/, '')
  return trimmed.endsWith('/api') ? trimmed.slice(0, -4) : trimmed
}

const SCGP_API_BASE = normalizeScgpApiBase(import.meta.env.VITE_SCGP_API_BASE || '')
const SCGP_TOKEN_USERNAME = import.meta.env.VITE_SCGP_TOKEN_USERNAME || ''
const SCGP_TOKEN_PASSWORD = import.meta.env.VITE_SCGP_TOKEN_PASSWORD || ''
const SCGP_COD_USUARIO_WIZ_RAW = import.meta.env.VITE_SCGP_COD_USUARIO_WIZ || ''

const CONTACT_REQUEST_TYPES: ScgpContactRequestType[] = [
  'contact_create',
  'contact_update',
  'contact_set_active',
  'contact_delete',
]

export function isContactChangeRequestType(requestType: string): requestType is ScgpContactRequestType {
  return (CONTACT_REQUEST_TYPES as string[]).includes(requestType)
}

function assertScgpEnvConfigured(): void {
  if (!SCGP_API_BASE) {
    throw new Error('Configure VITE_SCGP_API_BASE no .env para integração com o SCGP.')
  }
  if (!SCGP_TOKEN_USERNAME || !SCGP_TOKEN_PASSWORD) {
    throw new Error('Configure VITE_SCGP_TOKEN_USERNAME e VITE_SCGP_TOKEN_PASSWORD no .env.')
  }
  const codUsuarioWiz = Number(SCGP_COD_USUARIO_WIZ_RAW)
  if (!SCGP_COD_USUARIO_WIZ_RAW.trim() || !Number.isFinite(codUsuarioWiz)) {
    throw new Error('Configure VITE_SCGP_COD_USUARIO_WIZ (número inteiro) no .env.')
  }
}

const SCGP_COD_DEPARTAMENTO = 13

function getCodUsuarioWiz(): number {
  assertScgpEnvConfigured()
  return Number(SCGP_COD_USUARIO_WIZ_RAW)
}

function optionalString(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

function optionalPhoneDigits(value: string | null | undefined): string | undefined {
  if (value == null) return undefined
  const digits = value.replace(/\D/g, '')
  return digits ? digits : undefined
}

function extractCodContatoFromResponse(data: unknown): number | undefined {
  if (data == null || typeof data !== 'object') return undefined
  const record = data as Record<string, unknown>
  const candidates = [
    record.codContato,
    record.CodContato,
    (record.data as Record<string, unknown> | undefined)?.codContato,
    (record.data as Record<string, unknown> | undefined)?.CodContato,
    (record.result as Record<string, unknown> | undefined)?.codContato,
  ]
  for (const candidate of candidates) {
    const num = typeof candidate === 'number' ? candidate : Number(candidate)
    if (Number.isFinite(num) && num > 0) return num
  }
  return undefined
}

/** Falha se o body indicar erro de negócio mesmo com HTTP 2xx. */
function assertGerenciarPortalBusinessSuccess(rawResponse: unknown): void {
  if (rawResponse == null || typeof rawResponse !== 'object') return

  const record = rawResponse as Record<string, unknown>
  const sucesso = record.sucesso ?? record.Sucesso ?? record.success ?? record.Success
  if (sucesso === false) {
    const msg =
      record.mensagem ??
      record.Mensagem ??
      record.message ??
      record.Message ??
      record.erro ??
      record.Erro
    throw new Error(
      typeof msg === 'string' && msg.trim()
        ? msg
        : 'Operação rejeitada pelo SCGP (GerenciarPortal).'
    )
  }

  const erro = record.erro ?? record.Erro ?? record.error ?? record.Error
  if (typeof erro === 'string' && erro.trim()) {
    throw new Error(erro)
  }
}

function extractContactIdFromPayload(payload: ScgpChangePayload): string | null {
  const contactId = payload.contact_id
  return typeof contactId === 'string' && contactId.trim() ? contactId : null
}

async function parseScgpErrorMessage(response: Response): Promise<string> {
  try {
    const data = await response.json()
    if (typeof data === 'string' && data.trim()) return data
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>
      const message =
        record.message ??
        record.Message ??
        record.error_description ??
        record.error ??
        record.mensagem ??
        record.Mensagem
      if (typeof message === 'string' && message.trim()) return message
    }
  } catch {
    // ignore JSON parse errors
  }
  return `Erro HTTP ${response.status} ao comunicar com o SCGP.`
}

let cachedToken: { value: string; expiresAt: number } | null = null

function clearScgpTokenCache(): void {
  cachedToken = null
}

/** POST {VITE_SCGP_API_BASE}/api/token — grant_type=password (x-www-form-urlencoded). */
export async function getScgpAccessToken(forceRefresh = false): Promise<string> {
  assertScgpEnvConfigured()

  if (forceRefresh) {
    clearScgpTokenCache()
  }

  const now = Date.now()
  if (cachedToken && cachedToken.expiresAt > now + 30_000) {
    return cachedToken.value
  }

  const body = new URLSearchParams({
    grant_type: 'password',
    username: SCGP_TOKEN_USERNAME,
    password: SCGP_TOKEN_PASSWORD,
  })

  const response = await fetch(`${SCGP_API_BASE}/api/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })

  if (!response.ok) {
    throw new Error(await parseScgpErrorMessage(response))
  }

  const data = (await response.json()) as {
    access_token?: string
    expires_in?: number
  }

  if (!data.access_token) {
    throw new Error('Token de acesso SCGP não retornado pela API.')
  }

  const expiresInMs = (data.expires_in ?? 3600) * 1000
  cachedToken = { value: data.access_token, expiresAt: now + expiresInMs }
  return data.access_token
}

async function postGerenciarPortal(
  body: GerenciarPortalBody,
  token: string
): Promise<{ response: Response; rawResponse: unknown }> {
  const response = await fetch(`${SCGP_API_BASE}/api/Corretor/Contato/GerenciarPortal`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  let rawResponse: unknown = null
  try {
    rawResponse = await response.json()
  } catch {
    rawResponse = null
  }

  return { response, rawResponse }
}

export function buildGerenciarPortalBody(
  requestType: ScgpContactRequestType,
  payload: ScgpChangePayload,
  codCorretor: number,
  contact: BrokerContactForScgp | null
): GerenciarPortalBody {
  const codUsuarioWiz = getCodUsuarioWiz()
  const base = { codCorretor, codUsuarioWiz }

  switch (requestType) {
    case 'contact_create': {
      const nomContato = typeof payload.name === 'string' ? payload.name.trim() : ''
      if (!nomContato) {
        throw new Error('Nome do contato é obrigatório para inclusão no SCGP.')
      }
      return {
        ...base,
        tipoOperacao: 'I',
        nomContato,
        codDepartamento: SCGP_COD_DEPARTAMENTO,
        indSocio: 'N',
        dscCargo: optionalString(typeof payload.job_title === 'string' ? payload.job_title : undefined),
        numTelefone: optionalPhoneDigits(
          typeof payload.phone === 'string' ? payload.phone : undefined
        ),
        numWhatsapp: optionalString(
          typeof payload.whatsapp === 'string' ? payload.whatsapp : undefined
        ),
        dscEmails: optionalString(typeof payload.email === 'string' ? payload.email : undefined),
      }
    }
    case 'contact_update': {
      if (contact?.contact_code == null) {
        throw new Error('Contato sem contact_code (codContato SCGP). Não é possível alterar no portal.')
      }
      const nomContato = (
        typeof payload.name === 'string' ? payload.name : contact.name
      )?.trim()
      if (!nomContato) {
        throw new Error('Nome do contato é obrigatório para alteração no SCGP.')
      }
      return {
        ...base,
        tipoOperacao: 'A',
        codContato: contact.contact_code,
        nomContato,
        codDepartamento: SCGP_COD_DEPARTAMENTO,
        indSocio: contact.is_partner ? 'S' : 'N',
        dscCargo: optionalString(
          typeof payload.job_title === 'string' ? payload.job_title : contact.role
        ),
        numTelefone: optionalPhoneDigits(
          typeof payload.phone === 'string' ? payload.phone : contact.phone
        ),
        numWhatsapp: optionalString(
          typeof payload.whatsapp === 'string' ? payload.whatsapp : undefined
        ),
        dscEmails: optionalString(
          typeof payload.email === 'string' ? payload.email : contact.email
        ),
      }
    }
    case 'contact_delete': {
      if (contact?.contact_code == null) {
        throw new Error('Contato sem contact_code (codContato SCGP). Não é possível excluir no portal.')
      }
      return {
        ...base,
        tipoOperacao: 'E',
        codContato: contact.contact_code,
      }
    }
    case 'contact_set_active': {
      if (contact?.contact_code == null) {
        throw new Error('Contato sem contact_code (codContato SCGP). Não é possível ativar/desativar no portal.')
      }
      const isActive = payload.is_active === true
      if (!isActive) {
        return {
          ...base,
          tipoOperacao: 'E',
          codContato: contact.contact_code,
        }
      }
      const nomContato = contact.name?.trim()
      if (!nomContato) {
        throw new Error('Nome do contato é obrigatório para reativação no SCGP.')
      }
      return {
        ...base,
        tipoOperacao: 'A',
        codContato: contact.contact_code,
        nomContato,
        codDepartamento: SCGP_COD_DEPARTAMENTO,
        indSocio: contact.is_partner ? 'S' : 'N',
        dscCargo: optionalString(contact.role),
        numTelefone: optionalPhoneDigits(contact.phone),
        dscEmails: optionalString(contact.email),
      }
    }
  }
}

export async function gerenciarPortalContato(body: GerenciarPortalBody): Promise<GerenciarPortalResult> {
  assertScgpEnvConfigured()

  // 1) Token em /api/token — 2) GerenciarPortal com Bearer
  let token = await getScgpAccessToken()
  let { response, rawResponse } = await postGerenciarPortal(body, token)

  if (response.status === 401) {
    token = await getScgpAccessToken(true)
    ;({ response, rawResponse } = await postGerenciarPortal(body, token))
  }

  if (!response.ok) {
    let message = `Erro HTTP ${response.status} ao comunicar com o SCGP.`
    if (rawResponse != null && typeof rawResponse === 'object') {
      const record = rawResponse as Record<string, unknown>
      const apiMessage =
        record.message ??
        record.Message ??
        record.error_description ??
        record.error ??
        record.mensagem ??
        record.Mensagem
      if (typeof apiMessage === 'string' && apiMessage.trim()) {
        message = apiMessage
      }
    }
    throw new Error(message)
  }

  assertGerenciarPortalBusinessSuccess(rawResponse)

  const codContato =
    body.tipoOperacao === 'I' ? extractCodContatoFromResponse(rawResponse) : body.codContato

  return {
    tipoOperacao: body.tipoOperacao,
    codContato,
    rawResponse,
  }
}

export { extractContactIdFromPayload }
